import React, { useCallback, useEffect, useState } from 'react';
import './QuestionsPage.css';

interface RoundFeedbackEntry {
  id: string;
  roundId: string;
  roomName: string;
  gameType: string;
  playerId?: string;
  playerName: string;
  message: string;
  timestamp: number;
  questionId?: string;
  questionText?: string;
  bonusGameType?: string;
}

interface PlayerFeedbackPageProps {
  socket: { emit: (event: string, data: any) => void; on: (event: string, callback: (data: any) => void) => void; off: (event: string, callback?: (data: any) => void) => void; connected: boolean } | null;
  refreshToken?: number;
}

const GAME_TYPE_LABELS: Record<string, string> = {
  trivia: 'Trivia',
  'speed-challenge': 'Speed Challenge',
  'close-or-not': 'Close or Not',
  'picture-perfect': 'Picture Perfect',
  'drinking-game': 'Drinking Game',
  'pair-matching': 'Pair Matching',
  'bonus-level': 'Bonus Level',
  'odd-one-out': 'Odd One Out',
  'estimate-it': 'Estimate It',
  'true-or-silly': 'True or Silly?',
};

const PlayerFeedbackPage: React.FC<PlayerFeedbackPageProps> = ({ socket, refreshToken }) => {
  const [entries, setEntries] = useState<RoundFeedbackEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const fetchFeedback = useCallback(() => {
    if (!socket?.connected) return;
    setIsLoading(true);
    socket.emit('admin-get-round-feedback', {});
  }, [socket]);

  useEffect(() => {
    if (!socket) return;

    const handleRoundData = (data: { entries: RoundFeedbackEntry[] }) => {
      setEntries(data.entries || []);
      setIsLoading(false);
    };

    socket.on('admin-round-feedback-data', handleRoundData);
    return () => {
      socket.off('admin-round-feedback-data', handleRoundData);
    };
  }, [socket]);

  useEffect(() => {
    fetchFeedback();
    return undefined;
  }, [fetchFeedback, socket?.connected]);

  useEffect(() => {
    if (!socket?.connected) return;
    fetchFeedback();
  }, [fetchFeedback, refreshToken, socket?.connected]);

  const formatTimestamp = (ts: number) => new Date(ts).toLocaleString();

  const truncate = (text: string | undefined, len: number) => {
    if (!text) return '—';
    return text.length <= len ? text : text.slice(0, len) + '…';
  };

  const handleClearAll = () => {
    if (!socket?.connected) return;
    const confirmed = window.confirm(
      'This will clear all saved player feedback for all rooms. This cannot be undone. Continue?'
    );
    if (!confirmed) return;
    setIsClearing(true);
    socket.emit('admin-clear-round-feedback', {});
    setEntries([]);
    setIsClearing(false);
  };

  return (
    <div className="questions-page">
      <div className="questions-header">
        <h2 className="questions-title">
          <span className="title-prefix">&gt;</span> Player Feedback
        </h2>
        <div className="questions-header-row">
          <div className="questions-tabs">
            <div className="tab-button active">
              📝 All Feedback
              <span className="tab-count">{entries.length}</span>
            </div>
          </div>
          <button
            className={`tab-button questions-clear-button ${isClearing ? 'active' : ''}`}
            type="button"
            disabled={!socket?.connected || isClearing}
            onClick={handleClearAll}
          >
            🧹 Clear All
          </button>
        </div>
      </div>

      <div className="questions-content">
        {isLoading && entries.length === 0 ? (
          <div className="questions-loading">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="questions-empty">
            <span className="empty-icon">📝</span>
            <p>No player feedback yet</p>
          </div>
        ) : (
          <div className="feedback-list">
            {entries.map((entry) => (
              <div key={entry.id} className="feedback-card">
                <div className="feedback-card-header">
                  <span className="feedback-room">{entry.roomName}</span>
                  <span className="feedback-game">
                    {entry.bonusGameType
                      ? `Bonus: ${GAME_TYPE_LABELS[entry.bonusGameType] || entry.bonusGameType}`
                      : GAME_TYPE_LABELS[entry.gameType] || entry.gameType}
                  </span>
                  <span className="feedback-player" title="Player">
                    {truncate(entry.playerName?.trim() || entry.playerId || '—', 24)}
                  </span>
                  <span className="feedback-time">{formatTimestamp(entry.timestamp)}</span>
                </div>
                {entry.questionText && (
                  <div className="feedback-question">{truncate(entry.questionText, 140)}</div>
                )}
                <div className="feedback-answer">{entry.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerFeedbackPage;
